import React, { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Button } from "antd";
import {
  DashboardOutlined,
  DownOutlined,
  UpOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { DashboardMenuProps } from "./dashboard-menu";
import DashboardNavbar from "./dashboard-navbar";
import { useSidebarToggle } from "../hooks/use-sidebar-toggle";

interface DashboardLayoutProps {
  links?: DashboardMenuProps[];
  publicLinks?: DashboardMenuProps[];
}

const DashboardLayout = ({
  links = [],
  publicLinks = [],
}: DashboardLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [isPublicOpen, setIsPublicOpen] = useState(false);
  
  // Sidebar toggle for mobile responsiveness
  const { isSidebarOpen, isMobile, toggleSidebar, closeSidebar } = useSidebarToggle();

  // Auto-expand parent menu when on a child route
  React.useEffect(() => {
    const menusToExpand: string[] = [];
    
    links.forEach((link) => {
      if (link.children && link.children.length > 0) {
        // Check if any child route matches current path
        const hasActiveChild = link.children.some((child) =>
          location.pathname.startsWith(child.to)
        );
        
        if (hasActiveChild) {
          menusToExpand.push(link.title);
        }
      }
    });
    
    // Auto-expand public section if on a public route
    if (publicLinks.length > 0) {
      const hasActivePublicLink = publicLinks.some((link) =>
        location.pathname.startsWith(link.to)
      );
      if (hasActivePublicLink) {
        setIsPublicOpen(true);
      }
    }
    
    if (menusToExpand.length > 0) {
      setExpandedMenus((prev) => {
        // Merge with existing expanded menus to preserve manual expansions
        const newExpanded = [...new Set([...prev, ...menusToExpand])];
        // Only update if the arrays are different to prevent infinite loop
        if (JSON.stringify(prev.sort()) !== JSON.stringify(newExpanded.sort())) {
          return newExpanded;
        }
        return prev;
      });
    }
  }, [location.pathname]); // Removed links and publicLinks from dependencies

  const toggleMenu = (title: string) => {
    setExpandedMenus((prev) =>
      prev.includes(title)
        ? prev.filter((item) => item !== title)
        : [...prev, title]
    );
  };

  const togglePublicMenu = () => {
    setIsPublicOpen(!isPublicOpen);
  };

  // Enhanced active link detection - highlights parent routes during creation/editing
  const isActiveLink = (path: string) => {
    // Exact match for dashboard root
    if (path === "/dashboard" && location.pathname === "/dashboard") {
      return true;
    }
    
    // For other routes, check if current path starts with the link path
    // This ensures parent routes stay highlighted during add/edit operations
    // Example: /dashboard/features stays active for /dashboard/features/add or /dashboard/features/edit/123
    if (path !== "/dashboard") {
      return location.pathname.startsWith(path);
    }
    
    return false;
  };

  const isMenuExpanded = (title: string) => expandedMenus.includes(title);

  // Check if a parent menu has any active children
  const hasActiveChild = (children?: DashboardMenuProps[]) => {
    if (!children || children.length === 0) return false;
    return children.some((child) => location.pathname.startsWith(child.to));
  };

  // Handle link clicks - close sidebar on mobile
  const handleLinkClick = () => {
    if (isMobile) {
      closeSidebar();
    }
  };

  return (
    <div className="dashboard-layout">
      {/* Dashboard Navbar */}
      <DashboardNavbar onToggleSidebar={toggleSidebar} isMobile={isMobile} />
      
      {/* Mobile overlay */}
      {isMobile && isSidebarOpen && (
        <div 
          className="dashboard-sidebar-overlay active" 
          onClick={closeSidebar}
        />
      )}
      
      <div className="dashboard-body">
        <aside className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}>
          {/* Sidebar Header */}

          <ul className="sidebar-menu">
            {/* Dashboard */}
            <li>
              <Button
                icon={<DashboardOutlined />}
                type={isActiveLink("/dashboard") ? "primary" : "default"}
                className="dashboard-button"
                block
                onClick={() => {
                  navigate("/dashboard");
                  handleLinkClick();
                }}
              >
                Dashboard
              </Button>
            </li>

            {/* Protected Links */}
            {links.map((link, i) => (
              <li key={i}>
                {link.children && link.children.length > 0 ? (
                  // Parent with children - expandable
                  <div>
                    <div
                      onClick={() => toggleMenu(link.title)}
                      className={`menu-item-parent ${isMenuExpanded(link.title) ? "expanded" : ""} ${hasActiveChild(link.children) ? "has-active-child" : ""}`}
                    >
                      <div className="menu-item-content">
                        <span className="menu-icon">{link.src}</span>
                        <span className="menu-title">{link.title}</span>
                      </div>
                      {isMenuExpanded(link.title) ? (
                        <UpOutlined className="menu-arrow" />
                      ) : (
                        <DownOutlined className="menu-arrow" />
                      )}
                    </div>

                    {/* Child Links */}
                    {isMenuExpanded(link.title) && (
                      <ul className="menu-children">
                        {link.children.map((child, j) => (
                          <li key={j}>
                            <Link
                            
                              to={child.to}
                              className={`menu-child-link ${isActiveLink(child.to) ? "active" : ""}`}
                              onClick={handleLinkClick}
                            >
                              {child.src && (
                                <span className="menu-icon">{child.src}</span>
                              )}
                              <span className="menu-title">{child.title}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  // Simple link without children
                  <Link
                    to={link.to}
                    className={`menu-item-link ${isActiveLink(link.to) ? "active" : ""}`}
                    onClick={handleLinkClick}
                  >
                    <span className="menu-icon">{link.src}</span>
                    <span className="menu-title">{link.title}</span>
                  </Link>
                )}
              </li>
            ))}
            {/* Public Routes Section */}
            {publicLinks.length > 0 && (
              <li className="public-section">
                <div
                  onClick={togglePublicMenu}
                  className={`public-header ${isPublicOpen ? "expanded" : ""} ${hasActiveChild(publicLinks) ? "has-active-child" : ""}`}
                >
                  <div className="public-header-content">
                    <GlobalOutlined className="public-icon" />
                    <span className="public-title">Public</span>
                  </div>
                  {isPublicOpen ? (
                    <UpOutlined className="public-arrow" />
                  ) : (
                    <DownOutlined className="public-arrow" />
                  )}
                </div>

                {/* Public Links - Collapsible */}
                {isPublicOpen && (
                  <ul className="public-links">
                    {publicLinks.map((link, i) => (
                      <li key={i}>
                        <Link
                          to={link.to}
                          className={`public-link-item ${isActiveLink(link.to) ? "active" : ""}`}
                          onClick={handleLinkClick}
                        >
                          {link.src && (
                            <span className="link-icon">{link.src}</span>
                          )}
                          <span className="link-title">{link.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
          </ul>
        </aside>

        {/* Main content */}
        <main className="dashboard-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;